import type { EndpointMode } from "./types";

export const LOCAL_API_URL = "ws://localhost:8787/granular";
export const PRODUCTION_API_URL =
  "wss://cf-api-gateway.arthur6084.workers.dev/granular";
export const DEFAULT_LOCAL_API_KEY =
  "gn_sk_tenant_default_principal_local_e2e_00000000";

export const LOCAL_AUTH_URL = "http://localhost:3000";
export const PRODUCTION_AUTH_URL = "https://app.granular.software";

type ResolvedEndpointMode = "local" | "production";

function readEnv(name: string): string | undefined {
  if (typeof process === "undefined" || !process.env) return undefined;
  return process.env[name];
}

function normalizeMode(value?: string): EndpointMode | undefined {
  if (!value) return undefined;
  const normalized = value.trim().toLowerCase();
  if (normalized === "local") return "local";
  if (normalized === "prod" || normalized === "production") return "production";
  if (normalized === "auto") return "auto";
  return undefined;
}

function isTruthy(value?: string): boolean {
  if (!value) return false;
  const normalized = value.trim().toLowerCase();
  return (
    normalized === "1" ||
    normalized === "true" ||
    normalized === "yes" ||
    normalized === "on"
  );
}

export function isLocalApiUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1";
  } catch {
    return false;
  }
}

/**
 * Local dev + `sk_*` (WorkOS org key in env): map to the local gn_sk e2e key by default so every
 * request (HTTP + WebSocket) uses the same tenant as the v2 UI on localhost (`default`). Without
 * this, the SDK passes `sk_*` through → gateway resolves org_… while the UI still uses gn_sk →
 * you only see old default-tenant sandboxes and new ingest data looks “missing”.
 *
 * - `GRANULAR_LOCAL_API_KEY` — explicit key to use instead (e.g. another gn_sk).
 * - `GRANULAR_DISABLE_LOCAL_API_KEY_FALLBACK=1` — do not swap; send the real `sk_*` (org tenant +
 *   WorkOS validation on the gateway). Use when the UI is also on that org (e.g. session auth +
 *   NEXT_PUBLIC_DISABLE_LOCAL_API_KEY_FALLBACK on the app).
 */
export function resolveAuthTokenForApiUrl(
  authToken: string,
  apiUrl: string,
): string {
  if (
    !authToken.startsWith("sk_") ||
    !isLocalApiUrl(apiUrl) ||
    isTruthy(readEnv("GRANULAR_DISABLE_LOCAL_API_KEY_FALLBACK"))
  ) {
    return authToken;
  }

  const override = readEnv("GRANULAR_LOCAL_API_KEY")?.trim();
  return override || DEFAULT_LOCAL_API_KEY;
}

export function resolveEndpointMode(
  explicitMode?: EndpointMode,
): ResolvedEndpointMode {
  const explicit = normalizeMode(explicitMode);
  if (explicit === "local" || explicit === "production") {
    return explicit;
  }

  const envMode = normalizeMode(
    readEnv("GRANULAR_ENDPOINT_MODE") || readEnv("GRANULAR_ENV"),
  );
  if (envMode === "local" || envMode === "production") {
    return envMode;
  }

  if (
    isTruthy(readEnv("GRANULAR_USE_LOCAL_ENDPOINTS")) ||
    isTruthy(readEnv("GRANULAR_LOCAL"))
  ) {
    return "local";
  }
  if (
    isTruthy(readEnv("GRANULAR_USE_PRODUCTION_ENDPOINTS")) ||
    isTruthy(readEnv("GRANULAR_PROD"))
  ) {
    return "production";
  }

  return readEnv("NODE_ENV") === "development" ? "local" : "production";
}

export function resolveApiUrl(
  explicitApiUrl?: string,
  mode?: EndpointMode,
): string {
  if (explicitApiUrl) {
    return explicitApiUrl;
  }

  return resolveEndpointMode(mode) === "local"
    ? LOCAL_API_URL
    : PRODUCTION_API_URL;
}

export function resolveAuthUrl(
  explicitAuthUrl?: string,
  mode?: EndpointMode,
): string {
  if (explicitAuthUrl) {
    return explicitAuthUrl;
  }

  return resolveEndpointMode(mode) === "local"
    ? LOCAL_AUTH_URL
    : PRODUCTION_AUTH_URL;
}
